<script lang="ts" setup>
import {computed, defineAsyncComponent, onMounted, Ref, ref} from "vue";

import linkifyStr from "linkify-string";
import {whatsAppParser} from "@src/helpers/messageHelper";
import MessagePreview from "@src/components/views/HomeView/MainArea/ChatArea/Message/MessagePreview.vue";
import {IRecording} from "@src/types";

const Recording = defineAsyncComponent(() => import("@src/components/views/HomeView/MainArea/ChatArea/Message/Recording.vue"));
import GroupMessage from "@src/types/GroupMessage";
import Group from "@src/types/Group";
import Media from "@src/components/views/HomeView/MainArea/ChatArea/Message/Media.vue";
import MessageContextMenu from "@src/components/views/HomeView/MainArea/ChatArea/Message/MessageContextMenu.vue";
import {EllipsisVerticalIcon} from "@heroicons/vue/24/outline";
import {Capacitor} from "@capacitor/core";
import LinkPreview from "@src/components/views/HomeView/MainArea/ChatArea/Message/LinkPreview.vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import GroupMember from "@src/types/GroupMember";

const props = defineProps<{
    message: GroupMessage;
    followUp: boolean;
    self: boolean;
    divider?: boolean;
    session: Group
}>();

const url = ref('')
const caption = ref('')
const sessionStore = useChatSessionStore()

onMounted(() => {
    if (props.message.type === 'text' || props.message.type === 'warning') {
        url.value = extractURL(props.message.content)
        caption.value = props.message.content.replace(url.value, '').trim()
    }
})

function extractURL(str: string) {
    // Regular expression pattern to match URLs
    var urlPattern = /(https?:\/\/[^\s]+)/;
    // Match the URL pattern in the string
    var match = str.match(urlPattern);
    // If a URL is found, return it, otherwise return null
    return match ? match[0] : '';
}

const groupMembers = computed(() => {
    return props.session?.members
})

const groupMember = (id: number): GroupMember | null => {
    if (!groupMembers.value) {
        return null
    }
    return groupMembers.value.find((m: GroupMember) => m.id === id) ?? null
}

const sender = computed(() => {
    const msg: any = props.message
    const member = groupMember(msg.sender?.id ?? msg.costumer_id ?? msg.user?.id)

    if (msg.sender) {
        return {
            id: msg.sender.id ?? 0,
            name: msg.sender.name ?? "",
            avatar: msg.sender.avatar ?? "",
            type: msg.sender.type ?? "user",
            token: member?.token
        }
    }

    if (msg.costumer_id !== null) {
        const costumer = props.session.members.find(
            (member: any) => Number(member.id) === Number(msg.costumer_id)
        )

        return {
            id: costumer?.id ?? 0,
            name: costumer?.name ?? "",
            avatar: costumer?.profile_picture ?? "",
            type: "costumer",
            token: member?.token
        }
    }

    if (msg.user) {
        return {
            id: msg.user.id ?? 0,
            name: msg.user.name ?? "",
            avatar: msg.user.profile_photo_path ?? msg.user.profile_photo_url ?? "",
            type: "user",
            token: member?.token
        }
    }

    return {
        id: 0,
        name: "Sistema",
        avatar: "",
        type: "system",
        token: ''
    }
})

const getMemberColor = (memberId: number) => {
    if (props.self) {
        return {
            background: 'bg-green-50 dark:bg-green-900/20',
            backgroundStyle: '',
            text: 'text-green-700 dark:text-green-400',
            textStyle: ''
        }
    }

    const hue = (memberId * 137.508) % 360;

    const adjustedHue = hue >= 80 && hue <= 140 ? hue + 60 : hue;

    return {
        background: '',
        backgroundStyle: `--member-hue: ${adjustedHue}`,
        text: '',
        textStyle: `--member-hue: ${adjustedHue}`
    }
}

const memberColors = computed(() => getMemberColor(sender.value?.id ?? 0))
const showContextMenu = ref(false);

const contextMenuCoordinations: Ref<{ x: number; y: number }> = ref({
    x: 0,
    y: 0,
});

const showContextMenuAt = (pageX: number, pageY: number) => {
    showContextMenu.value = true;
    contextMenuCoordinations.value = {
        x: window.innerWidth - 220 <= pageX ? window.innerWidth - 250 : pageX,
        y: window.innerHeight - 300 <= pageY ? window.innerHeight - 250 : pageY,
    };
};

const handleShowContextMenu = (event: MouseEvent) => {
    showContextMenuAt(event.pageX, event.pageY);
};

const isNative = Capacitor.isNativePlatform();

const handleDotsMenuOpen = (event: MouseEvent) => {
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 200;

    let x = rect.left;
    let y = rect.bottom + 4;

    if (x + menuWidth > window.innerWidth) x = window.innerWidth - menuWidth - 8;
    if (y + menuHeight > window.innerHeight) y = rect.top - menuHeight - 4;

    showContextMenu.value = true;
    contextMenuCoordinations.value = {x, y};
};

// closes the context menu
const handleCloseContextMenu = () => {
    showContextMenu.value = false;
};

// close the context menu when opening a new one.
const contextConfig = {
    handler: handleCloseContextMenu,
    events: ["contextmenu"],
};

// decide whether to show or hide the avatar next to the image.
const hideAvatar = () => {
    if (props.divider && !props.self && props.message.type !== 'warning') {
        return false;
    } else {
        if (props.followUp) {
            return true;
        }
        if (props.self) {
            return true;
        }
    }
};

const formatDate = () => {
    const createdAt = new Date(props.message.created_at)
    return createdAt.toLocaleString('pt-BR', {hour: '2-digit', minute: '2-digit'});
}

const ballonClass = () => {
    const baseClasses = props.self
        ? 'rounded-tl-xl ml-4 order-2'
        : 'rounded-tr-xl mr-4';

    if (props.self) {
        return `${baseClasses} ${memberColors.value.background}`;
    }

    return baseClasses;
}

const ballonStyle = computed(() => {
    return props.self ? '' : memberColors.value.backgroundStyle;
})

const nameStyle = computed(() => {
    return props.self ? '' : memberColors.value.textStyle;
})
</script>

<template>
    <div :id="`message-${props.message.id}`"
         :message-id="`${props.message.id}`">

        <div v-if="props.message.type === 'warning'" class="flex items-center justify-center mb-4">
            <div
                class="rounded-xl mr-4 bg-gray-300 dark:bg-gray-900 text-gray-700 dark:text-gray-200 group relative max-w-125 p-5 rounded-b-xl transition duration-500"
                v-html="whatsAppParser(props.message.content)">
            </div>
        </div>
        <div v-else
             :class="{ 'justify-end': props.self }"
             class="xs:mb-6 md:mb-5 flex">
            <!--avatar-->
            <div :class="{ 'ml-[2.25rem]': props.followUp && !divider }" class="mr-4">
                <div
                    v-if="!hideAvatar()"
                    :aria-label="sender?.name"
                    class="outline-none"
                >
                    <div
                        :style="{ backgroundImage: `url(${sender?.avatar})` }"
                        class="w-[2.25rem] h-[2.25rem] bg-cover bg-center rounded-full"
                    ></div>
                </div>
            </div>

            <div class="flex items-end min-w-0">
                <!--bubble-->
                <div
                    v-click-outside="contextConfig"
                    :class="ballonClass()"
                    :style="ballonStyle"
                    class="group max-w-125 min-w-0 p-5 rounded-b-xl transition duration-500 member-message-bg select-none"
                    style="-webkit-touch-callout: none;"
                    @click="handleCloseContextMenu"
                    @contextmenu.prevent="handleShowContextMenu"
                >
                    <!--reply to-->
                    <MessagePreview
                        v-if="props.message.reply_to && props.message.replied_message"
                        :input="false"
                        :message="props.message.replied_message"
                        :self="props.self"
                        :session="props.session" class="mb-5 px-3"
                    />

                    <!--content-->
                    <button
                        v-if="sender?.name"
                        :class="props.self ? memberColors.text + '' : ' cursor-pointer hover:font-semibold '"
                        :disabled="props.self"
                        :style="nameStyle"
                        class="font-medium member-name-color"
                        @click="sessionStore.setStartConversationData({id: sender.id, name: sender?.name, token: sender.token ?? ''})"
                    >
                        {{ sender?.name }}
                    </button>
                    <p
                        v-if="props.message.content  && (props.message.type === 'text' || props.message.type === 'list') && !url"
                        class="body-2 outline-none text-black opacity-60 dark:text-white dark:opacity-70 break-words"
                        tabindex="0"
                        v-html="
                          whatsAppParser(linkifyStr(props.message.content  as string, {
                            className: props.self
                              ? 'text-black opacity-50'
                              : 'text-indigo-500 dark:text-indigo-300',
                            format: {
                              url: (value) =>
                                value.length > 50 ? value.slice(0, 50) + `…` : value,
                            },
                          }))
                        "
                    ></p>

                    <!--recording-->
                    <div
                        v-else-if="props.message.content && props.message.type === 'recording'"
                    >
                        <!--                    >-->
                        <Recording
                            :recording="<IRecording><unknown>message"
                            :self="props.self"
                        />
                    </div>

                    <!--attachments-->
                    <Media
                        v-if="props.message.type !== 'text'"
                        :message="props.message"
                        :preview="false"
                        :self="props.self"
                    />

                    <LinkPreview v-else-if="url" :caption="caption" :is-preview="true" :url="url"/>

                </div>

                <!-- dots para mensagem própria: sem order (order:0), depois do bubble no DOM → aparece entre bubble e date(order:1) -->
                <button
                    v-if="isNative && props.self && props.message.type !== 'warning'"
                    class="self-end mb-0.5 p-1 text-gray-400 dark:text-gray-500 flex-shrink-0"
                    @click.stop="handleDotsMenuOpen"
                >
                    <EllipsisVerticalIcon class="w-5 h-5"/>
                </button>

                <!--date-->
                <div
                    :class="props.self ? ['ml-4', 'order-1'] : ['mr-4', 'order-1']">
                    <p class="body-1 text-black/70 dark:text-white/70 whitespace-pre">
                        {{ formatDate() }}
                    </p>
                </div>

                <!-- dots para customer: order-2, depois da date(order:1) -->
                <button
                    v-if="isNative && !props.self && props.message.type !== 'warning'"
                    class="self-end mb-0.5 p-1 text-gray-400 dark:text-gray-500 flex-shrink-0 order-2"
                    @click.stop="handleDotsMenuOpen"
                >
                    <EllipsisVerticalIcon class="w-5 h-5"/>
                </button>
            </div>
        </div>
        <MessageContextMenu
            :handle-close-context-menu="handleCloseContextMenu"
            :isGroup="true"
            :left="contextMenuCoordinations.x"
            :message="props.message"
            :self="props.self"
            :show="showContextMenu"
            :top="contextMenuCoordinations.y"
            @close-context-menu="showContextMenu = false"/>
    </div>
</template>

<style scoped>
.member-message-bg {
    background-color: hsl(var(--member-hue, 0), 70%, 95%);
}

.dark .member-message-bg {
    background-color: hsl(var(--member-hue, 0), 40%, 20%);
}

.member-name-color {
    color: hsl(var(--member-hue, 0), 70%, 40%);
}

.dark .member-name-color {
    color: hsl(var(--member-hue, 0), 60%, 75%);
}
</style>